/* ==========================================================================
   Purple Rinse — product behaviour
   One source of truth for the selected variant. The main form, the sticky
   mobile bar and the gallery all read from the same `variant:change` event,
   so there is never a second purchase state to keep in sync.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------ quantity input --- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener(
        'click',
        function (event) {
          var button = event.target.closest('button[data-qty]');
          if (!button) return;
          event.preventDefault();
          var step = button.dataset.qty === 'plus' ? 1 : -1;
          var min = parseInt(this.input.min, 10) || 1;
          var max = parseInt(this.input.max, 10) || 9999;
          var next = (parseInt(this.input.value, 10) || min) + step;
          this.input.value = Math.min(max, Math.max(min, next));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }.bind(this)
      );

      this.input.addEventListener(
        'change',
        function () {
          document.dispatchEvent(
            new CustomEvent('quantity:change', {
              detail: { value: parseInt(this.input.value, 10) || 1, sectionId: this.dataset.sectionId }
            })
          );
        }.bind(this)
      );
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* -------------------------------------------------------- variant picker --- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl === 'true';

      var data = this.querySelector('[data-variant-data]');
      this.variants = data ? JSON.parse(data.textContent) : [];

      this.addEventListener('change', this.onChange.bind(this));

      this.onQuantityChange = function (event) {
        if (event.detail.sectionId && event.detail.sectionId !== this.sectionId) return;
        this.quantity = event.detail.value;
        this.publish();
      }.bind(this);
      document.addEventListener('quantity:change', this.onQuantityChange);

      this.quantity = 1;
      /* Announce the server-rendered variant once so the sticky bar and the
         gallery start from the same state as the form. */
      this.publish();
    }

    disconnectedCallback() {
      document.removeEventListener('quantity:change', this.onQuantityChange);
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-position]'))
        .filter(function (el) {
          if (el.tagName === 'SELECT') return true;
          return el.checked;
        })
        .sort(function (a, b) {
          return a.dataset.optionPosition - b.dataset.optionPosition;
        })
        .map(function (el) {
          return el.value;
        });
    }

    get currentVariant() {
      var selected = this.selectedOptions;
      return (
        this.variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return option === selected[index];
          });
        }) || null
      );
    }

    onChange() {
      this.publish();
      var variant = this.currentVariant;
      if (this.updateUrl && variant && window.history.replaceState) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }
    }

    publish() {
      var variant = this.currentVariant;
      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: {
            sectionId: this.sectionId,
            variant: variant,
            quantity: this.quantity || 1,
            mediaId: variant && variant.featured_media ? variant.featured_media.id : null
          }
        })
      );
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------ variant display --- */
  /* Anything that has to redraw when the variant changes: price, unit price,
     availability, the hidden variant id, the sticky bar. */
  class VariantSubscriber extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onVariantChange = this.handle.bind(this);
      document.addEventListener('variant:change', this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
    }

    handle(event) {
      if (this.sectionId && event.detail.sectionId && event.detail.sectionId !== this.sectionId) return;
      this.render(event.detail);
    }

    render() {}
  }

  class ProductPrice extends VariantSubscriber {
    render(detail) {
      var variant = detail.variant;
      var now = this.querySelector('[data-price-now]');
      var was = this.querySelector('[data-price-was]');
      var save = this.querySelector('[data-price-save]');
      var unit = this.querySelector('[data-price-unit]');

      if (!variant) {
        this.setAttribute('data-unavailable', 'true');
        return;
      }
      this.removeAttribute('data-unavailable');

      if (now) now.textContent = window.Theme.formatMoney(variant.price);

      var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (was) {
        was.hidden = !hasCompare;
        if (hasCompare) was.textContent = window.Theme.formatMoney(variant.compare_at_price);
      }
      if (save) {
        save.hidden = !hasCompare;
        if (hasCompare) {
          save.textContent = save.dataset.template.replace(
            '%amount%',
            window.Theme.formatMoney(variant.compare_at_price - variant.price)
          );
        }
      }
      if (unit) {
        if (variant.unit_price) {
          unit.hidden = false;
          unit.textContent =
            window.Theme.formatMoney(variant.unit_price) +
            ' / ' +
            (variant.unit_price_measurement ? variant.unit_price_measurement.reference_unit : '');
        } else {
          unit.hidden = true;
        }
      }
    }
  }
  if (!customElements.get('product-price')) customElements.define('product-price', ProductPrice);

  class BuyButtons extends VariantSubscriber {
    connectedCallback() {
      super.connectedCallback();
      this.idInput = this.querySelector('input[name="id"]');
      this.button = this.querySelector('[data-add-to-cart]');
      this.label = this.querySelector('[data-add-to-cart-text]');
    }

    render(detail) {
      var variant = detail.variant;
      if (!this.button) return;

      if (!variant) {
        this.button.disabled = true;
        if (this.label) this.label.textContent = this.button.dataset.unavailableText;
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;

      if (!variant.available) {
        this.button.disabled = true;
        if (this.label) this.label.textContent = this.button.dataset.soldOutText;
      } else {
        this.button.disabled = false;
        if (this.label) this.label.textContent = this.button.dataset.addText;
      }
    }
  }
  if (!customElements.get('buy-buttons')) customElements.define('buy-buttons', BuyButtons);

  class StickyPurchaseBar extends VariantSubscriber {
    connectedCallback() {
      super.connectedCallback();
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.nameEl = this.querySelector('[data-sticky-name]');
      this.button = this.querySelector('button');

      /* The bar never owns a cart action of its own: it submits the real form. */
      if (this.button) {
        this.button.addEventListener(
          'click',
          function () {
            var form = document.getElementById(this.dataset.formId);
            if (!form) return;
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else form.submit();
          }.bind(this)
        );
      }

      var anchor = document.getElementById(this.dataset.watch);
      if (anchor && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (entry) {
                this.classList.toggle('is-visible', !entry.isIntersecting);
              }.bind(this)
            );
          }.bind(this),
          { rootMargin: '-90px 0px 0px 0px' }
        );
        this.observer.observe(anchor);
      } else {
        this.classList.add('is-visible');
      }

      document.body.classList.add('has-sticky-bar');
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-bar');
    }

    render(detail) {
      var variant = detail.variant;
      if (!variant) return;
      var quantity = detail.quantity || 1;
      if (this.priceEl) this.priceEl.textContent = window.Theme.formatMoney(variant.price * quantity);
      if (this.nameEl) this.nameEl.textContent = variant.title === 'Default Title' ? this.dataset.fallbackName : variant.title;
      if (this.button) this.button.disabled = !variant.available;
    }
  }
  if (!customElements.get('sticky-purchase-bar')) customElements.define('sticky-purchase-bar', StickyPurchaseBar);

  /* ------------------------------------------------------------ add form --- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      if (this.dataset.ajax !== 'true') return;

      event.preventDefault();
      var button = this.querySelector('[data-add-to-cart]');
      if (button && button.disabled) return;

      if (button) button.setAttribute('aria-busy', 'true');
      if (this.errorEl) this.errorEl.classList.remove('is-visible');

      var body = new FormData(this.form);

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json };
          });
        })
        .then(
          function (result) {
            if (!result.ok) {
              this.showError(result.json.description || result.json.message);
              return;
            }
            document.dispatchEvent(
              new CustomEvent('cart:added', { detail: { item: result.json, sections: result.json.sections } })
            );
          }.bind(this)
        )
        .catch(
          function () {
            this.showError(this.dataset.genericError);
          }.bind(this)
        )
        .finally(
          function () {
            if (button) button.removeAttribute('aria-busy');
          }
        );
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.genericError;
      this.errorEl.classList.add('is-visible');
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
})();
